import { BaseEmissionFactors, FPPTEmissionFactors } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import { capitalizeFirstLetter, cn } from '@shared/lib/utils';
import { useFormContext } from 'react-hook-form';

interface LifeCycleStageModellingProps {
  className?: string;
  allowCustomValues?: boolean;
  showCustomSelector?: boolean;
  showSourceColumn?: boolean;
  baseEmissionFactors?: BaseEmissionFactors | FPPTEmissionFactors;
}

function LifeCycleStageModellingHeader({
  showCustomSelector = true,
  showSourceColumn = true,
}: {
  showCustomSelector?: boolean;
  showSourceColumn?: boolean;
}) {
  return (
    <div className="col-span-full grid grid-cols-12 gap-2">
      <Text
        className={cn('col-span-2 font-medium', {
          'col-span-5': showCustomSelector,
        })}
      >
        Life-cycle stage
      </Text>
      <Text
        className={cn('col-span-4 font-medium', {
          hidden: !showSourceColumn,
        })}
      >
        Source
      </Text>
      <span className="col-span-3 flex items-center gap-1 text-nowrap">
        <Text className="font-medium">Emission factor </Text>
        <Unit variant="kgCO2e/kg" />
      </span>
    </div>
  );
}

function LifeCycleStageRow({
  stage,
  allowCustomValues,
  baseEmissionFactor,
  showCustomSelector,
  showSourceColumn = true,
}: {
  stage: {
    name: string;
    icon: React.ReactNode;
  };
  allowCustomValues?: boolean;
  showCustomSelector?: boolean;
  baseEmissionFactor?: BaseEmissionFactors[keyof BaseEmissionFactors];
  showSourceColumn?: boolean;
}) {
  const { getValues, setValue, resetField, control } = useFormContext();

  const method = getValues(`emissionFactors.${stage.name}.method`);

  return (
    <div className="col-span-full grid grid-cols-12 gap-2">
      <div className="col-span-2 flex items-center gap-2">
        {stage.icon}
        <Text className=" font-medium">
          {capitalizeFirstLetter(stage.name)}
        </Text>
      </div>
      {showCustomSelector && (
        <SelectFormField
          name={`emissionFactors.${stage.name}.method`}
          className="col-span-3"
          options={[
            { label: 'Standard value', value: 'standard' },
            { label: 'Custom value', value: 'custom' },
          ]}
          handleSelect={(method) => {
            if (method === 'standard') {
              setValue(`emissionFactors.${stage.name}`, {
                method: 'standard',
                value: baseEmissionFactor?.value,
                source: baseEmissionFactor?.source || '',
              });
              return;
            }

            if (
              control._defaultValues?.emissionFactors?.[stage.name].method ===
              'custom'
            ) {
              resetField(`emissionFactors.${stage.name}`);
              return;
            }

            setValue(`emissionFactors.${stage.name}`, {
              method: 'custom',
              value: '',
              source: '',
            });
          }}
        />
      )}

      {showSourceColumn && (
        <InputFormField
          name={`emissionFactors.${stage.name}.source`}
          className="col-span-4"
          disabled={
            !allowCustomValues || (showCustomSelector && method === 'standard')
          }
        />
      )}

      <InputFormField
        name={`emissionFactors.${stage.name}.value`}
        className="col-span-3"
        inputClassName="text-right"
        step="any"
        type="number"
        disabled={
          !allowCustomValues || (showCustomSelector && method === 'standard')
        }
      />
    </div>
  );
}

export default function LifeCycleStageModelling({
  allowCustomValues = false,
  showCustomSelector = false,
  showSourceColumn = true,
  baseEmissionFactors,
}: LifeCycleStageModellingProps) {
  return (
    <div className="grid grid-cols-12 gap-2">
      <LifeCycleStageModellingHeader
        showCustomSelector={showCustomSelector}
        showSourceColumn={showSourceColumn}
      />
      {[
        {
          name: 'farming',
          icon: <Icons.tractor className="size-4 text-teal-400" />,
        },
        {
          name: 'processing',
          icon: <Icons.factory className="size-4 text-teal-400" />,
        },
        {
          name: 'packaging',
          icon: <Icons.package className="size-4 text-teal-400" />,
        },
        {
          name: 'transport',
          icon: <Icons.truck className="size-4 text-teal-400" />,
        },
      ].map((stage) => (
        <LifeCycleStageRow
          key={stage.name}
          stage={stage}
          allowCustomValues={allowCustomValues}
          baseEmissionFactor={
            baseEmissionFactors![stage.name as keyof BaseEmissionFactors]
          }
          showCustomSelector={showCustomSelector}
          showSourceColumn={showSourceColumn}
        />
      ))}
    </div>
  );
}
